import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Clock, 
  TrendingUp, 
  Briefcase,
  Download,
  Calendar,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { format, subDays } from "date-fns";
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, LineChart, Line, Area, AreaChart 
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  feeEarning: "#10B981",
  nonFeeEarning: "#F59E0B",
  utilization: "#6366F1",
  projects: ["#EC4899", "#8B5CF6", "#06B6D4", "#F97316", "#84CC16", "#14B8A6", "#F43F5E"]
};

export default function ConsultantDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectEntries, setProjectEntries] = useState(null);
  const [entriesSort, setEntriesSort] = useState("desc"); // "desc" = newest first

  const decodedName = decodeURIComponent(name);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
      const endDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
      const params = startDate && endDate ? `?start_date=${startDate}&end_date=${endDate}` : "";

      const response = await axios.get(`${API}/consultant/${encodeURIComponent(decodedName)}${params}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching consultant data:", error);
      toast.error("Failed to load consultant data");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [decodedName, dateRange, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const { downloadFile } = await import("../lib/download");
      const startDate = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : "";
      const endDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
      const params = `?personnel=${encodeURIComponent(decodedName)}${startDate ? `&start_date=${startDate}` : ""}${endDate ? `&end_date=${endDate}` : ""}`;
      await downloadFile(`${API}/export/csv${params}`);
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const fetchProjectEntries = (projectId) => {
    if (selectedProjectId === projectId) {
      setSelectedProjectId(null);
      setProjectEntries(null);
      return;
    }
    setSelectedProjectId(projectId);
    // Filter entries for this project
    const filtered = (data?.entries || []).filter(e => e.project_id === projectId);
    filtered.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    setProjectEntries(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, company_breakdown, project_breakdown, daily_data, entries } = data;

  // Prepare pie chart data
  const pieData = [
    { name: "Fee Earning", value: summary.fee_earning_hours },
    { name: "Non-Fee Earning", value: summary.non_fee_earning_hours }
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="consultant-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">
                  {decodedName.split(" ").map(n => n[0]).join("")}
                </span>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">{decodedName}</h1>
                <p className="text-muted-foreground">Consultant Performance Analytics</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="date-filter-btn">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {dateRange.from && dateRange.to 
                    ? `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`
                    : "All Time"}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  setDateRange(range || { from: undefined, to: undefined });
                  if (range?.from && range?.to) {
                    setCalendarOpen(false);
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          <Button onClick={handleExport} className="gap-2" data-testid="export-btn">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="metric-card" data-testid="total-hours-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p>
                <p className="text-3xl font-bold text-foreground tabular-nums">{summary.total_hours}</p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <Clock className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="fee-earning-hours-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Fee Earning</p>
                <p className="text-3xl font-bold text-emerald-600 tabular-nums">{summary.fee_earning_hours}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="non-fee-hours-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Non-Fee Earning</p>
                <p className="text-3xl font-bold text-amber-600 tabular-nums">{summary.non_fee_earning_hours}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card" data-testid="utilization-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization</p>
                <p className="text-3xl font-bold text-foreground tabular-nums">{summary.utilization_rate}%</p>
              </div>
              <div className="p-3 rounded-lg bg-indigo-500/10">
                <Briefcase className="w-5 h-5 text-indigo-500" />
              </div>
            </div>
            <Progress value={summary.utilization_rate} className="mt-4 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Distribution Pie */}
        <Card data-testid="time-distribution-chart">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Time Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    labelLine={true}
                  >
                    <Cell fill={COLORS.feeEarning} />
                    <Cell fill={COLORS.nonFeeEarning} />
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [`${value} hours (${(props.payload.percent * 100).toFixed(1)}%)`, name]}
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hours by Category Bar Chart */}
        <Card data-testid="category-breakdown-chart">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Hours by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={company_breakdown} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Legend />
                  <Bar dataKey="fee_earning" name="Fee Earning" fill={COLORS.feeEarning} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="non_fee_earning" name="Non-Fee Earning" fill={COLORS.nonFeeEarning} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {daily_data.length > 0 && (
        <Card data-testid="daily-trend-chart">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Daily Hours Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily_data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorFee" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.feeEarning} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.feeEarning} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorNonFee" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.nonFeeEarning} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.nonFeeEarning} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => format(new Date(val), "MMM d")}
                  />
                  <YAxis />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                    labelFormatter={(val) => format(new Date(val), "MMM d, yyyy")}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="fee_earning" 
                    name="Fee Earning"
                    stroke={COLORS.feeEarning} 
                    fillOpacity={1} 
                    fill="url(#colorFee)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="non_fee_earning" 
                    name="Non-Fee Earning"
                    stroke={COLORS.nonFeeEarning} 
                    fillOpacity={1} 
                    fill="url(#colorNonFee)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Tabs */}
      <Tabs defaultValue="projects" data-testid="detail-tabs">
        <TabsList>
          <TabsTrigger value="projects" data-testid="projects-tab">Projects</TabsTrigger>
          <TabsTrigger value="entries" data-testid="entries-tab">All Entries</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th>Project ID</th>
                      <th>Company</th>
                      <th>Hours</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project_breakdown.map((project, index) => (
                      <tr key={project.project_id} 
                        data-testid={`project-row-${index}`} 
                        className={`cursor-pointer transition-[background-color] duration-150 ${selectedProjectId === project.project_id ? "bg-primary/10" : "hover:bg-muted/50"}`}
                        onClick={() => fetchProjectEntries(project.project_id)}
                      >
                        <td className="font-medium">{project.project_id}</td>
                        <td className="text-muted-foreground">{project.company}</td>
                        <td className="tabular-nums font-medium">{project.hours.toFixed(2)}</td>
                        <td>
                          <Badge 
                            className={project.fee_earning.includes("Non-Fee") ? "badge-non-fee-earning" : "badge-fee-earning"}
                          >
                            {project.fee_earning.includes("Non-Fee") ? "Non-Fee" : "Fee Earning"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Project Entries Drill-down */}
              {selectedProjectId && projectEntries && (
                <div className="mt-4 border border-primary/20 rounded-lg overflow-hidden animate-fade-in" data-testid="project-entries-drilldown">
                  <div className="p-3 bg-primary/5 border-b border-primary/20 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{decodedName} — Entries for {selectedProjectId}</p>
                      <p className="text-xs text-muted-foreground">{projectEntries.length} entries · {projectEntries.reduce((s, e) => s + (e.hours || 0), 0).toFixed(2)}h total</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedProjectId(null); setProjectEntries(null); }}>Close</Button>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Date</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Description</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Hours</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectEntries.map((e, i) => (
                          <tr key={`${e.date}-${i}`} className="border-b border-border/50 hover:bg-muted/20">
                            <td className="py-1.5 px-3 text-muted-foreground whitespace-nowrap">{e.date}</td>
                            <td className="py-1.5 px-3 max-w-[300px] truncate" title={e.description}>{e.description || "-"}</td>
                            <td className="py-1.5 px-3 text-right tabular-nums font-medium">{e.hours}</td>
                            <td className="py-1.5 px-3">
                              <Badge className={`text-[10px] px-1.5 py-0 ${(e.fee_earning || "").includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                {(e.fee_earning || "").includes("Non-Fee") ? "Non-Fee" : "Fee Earning"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                        {projectEntries.length === 0 && (
                          <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No entries found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="entries" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">{entries.length} total entries</p>
                <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setEntriesSort(s => s === "desc" ? "asc" : "desc")}>
                  <Calendar className="w-3 h-3" />
                  {entriesSort === "desc" ? "Newest First" : "Oldest First"}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th>Date</th>
                      <th>Project</th>
                      <th>Description</th>
                      <th>Hours</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...entries].sort((a, b) => entriesSort === "desc" ? (b.date || "").localeCompare(a.date || "") : (a.date || "").localeCompare(b.date || "")).slice(0, 100).map((entry, index) => (
                      <tr key={entry.id || index} data-testid={`entry-row-${index}`}>
                        <td className="text-muted-foreground whitespace-nowrap">
                          {entry.date}
                        </td>
                        <td className="font-medium">{entry.project_id || entry.company}</td>
                        <td className="max-w-[300px] truncate text-muted-foreground">{entry.description}</td>
                        <td className="tabular-nums font-medium">{entry.hours}</td>
                        <td>
                          <Badge 
                            className={(entry.fee_earning || "").includes("Non-Fee") ? "badge-non-fee-earning" : "badge-fee-earning"}
                          >
                            {(entry.fee_earning || "").includes("Non-Fee") ? "Non-Fee" : "Fee Earning"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {entries.length > 100 && (
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Showing 100 of {entries.length} entries. Export to see all.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
